import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useTranslation } from 'react-i18next';
import { useAuth } from "@/context/auth.context";
import { api } from "@/lib/api";
import { Loader2, Upload, User } from "lucide-react";
import { toast } from "sonner";

const profileSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(8, "Password must be at least 8 characters")
    .regex(/[0-9]/, "Password must include a number")
    .regex(/[^A-Za-z0-9]/, "Password must include a special character"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ProfileFormValues = z.infer<typeof profileSchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;

export default function AccountSettings() {
  const { user, setCurrentUser } = useAuth();
  const { t } = useTranslation();
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profilePreview, setProfilePreview] = useState<string>("");

  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
    },
  });

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Load user data on mount
  useEffect(() => {
    if (user) {
      profileForm.reset({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
      });
      if (user.profileUrl) {
        setProfilePreview(user.profileUrl);
      }
    }
  }, [user, profileForm]);

  const handleProfileSubmit = async (values: ProfileFormValues) => {
    if (!user?.id) {
      toast.error("User not found");
      return;
    }

    setProfileLoading(true);
    try {
      const updateData = {
        ...values,
        profileUrl: profilePreview,
      };

      const { data } = await api.patch(`/users/${user.id}`, updateData);
      
      // Update auth context with new user data
      setCurrentUser(data.data);
      
      toast.success(t("messages.profileUpdated"));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t("messages.profileUpdateFailed"));
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (_values: PasswordFormValues) => {
    if (!user?.id) {
      toast.error("User not found");
      return;
    }

    setPasswordLoading(true);
    try {
      // Note: Password changes require verification of current password
      // The backend may need a dedicated password change endpoint
      // For now, try using reset-password endpoint or show appropriate message
      toast.info("Password change functionality requires backend support. Please contact your administrator.");
      
      // TODO: Implement password change endpoint
      // await api.post("/auth/change-password", {
      //   currentPassword: values.currentPassword,
      //   newPassword: values.newPassword,
      // });

      passwordForm.reset();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleProfileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (1MB max)
      if (file.size > 1 * 1024 * 1024) {
        toast.error("File size must be less than 1MB");
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please upload an image file");
        return;
      }

      // Read file and convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setProfilePreview(base64String);
        toast.success("Profile picture uploaded successfully");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancel = () => {
    if (user) {
      profileForm.reset({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
      });
      setProfilePreview(user.profileUrl || "");
    }
      toast.info(t("messages.changesDiscarded"));
  };

  const getInitials = () => {
    const firstName = profileForm.watch("first_name") || user?.first_name || "";
    const lastName = profileForm.watch("last_name") || user?.last_name || "";
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return user?.name?.[0]?.toUpperCase() || "U";
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="container max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("pages.accountSettings.title")}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {t("pages.accountSettings.subtitle")}
          </p>
        </div>

        {/* Personal Information */}
        <Form {...profileForm}>
          <form onSubmit={profileForm.handleSubmit(handleProfileSubmit)}>
            <Card className="rounded-lg shadow-sm">
              <CardHeader>
                <CardTitle>{t("pages.accountSettings.personalInformation")}</CardTitle>
                <CardDescription>
                  {t("pages.accountSettings.personalInfoDescription")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Picture Upload */}
                <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
                  <Avatar className="w-20 h-20">
                    {profilePreview ? (
                      <AvatarImage src={profilePreview} alt="Profile picture" />
                    ) : (
                      <AvatarFallback>
                        {user?.profileUrl ? (
                          <AvatarImage src={user.profileUrl} alt="Profile" />
                        ) : (
                          <User className="h-10 w-10" />
                        )}
                        {!user?.profileUrl && <span className="text-xl">{getInitials()}</span>}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="space-y-2">
                    <input
                      type="file"
                      id="profile-upload"
                      accept="image/*"
                      className="hidden"
                      onChange={handleProfileUpload}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => document.getElementById('profile-upload')?.click()}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {t("pages.accountSettings.uploadNewPicture")}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      {t("pages.accountSettings.pictureFormat")}
                    </p>
                  </div>
                </div>

                {/* First Name and Last Name */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={profileForm.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("pages.accountSettings.firstName")}</FormLabel>
                        <FormControl>
                          <Input placeholder={t("pages.accountSettings.firstName")} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("pages.accountSettings.lastName")}</FormLabel>
                        <FormControl>
                          <Input placeholder={t("pages.accountSettings.lastName")} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Email */}
                <FormField
                  control={profileForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("pages.accountSettings.emailAddress")}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="contact@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        {t("pages.accountSettings.emailDescription")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
              <CardFooter className="flex justify-between border-t px-6 py-4">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={profileLoading}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={profileLoading}
                >
                  {profileLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("common.saveChanges")}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </Form>

        {/* Change Password */}
        <Form {...passwordForm}>
          <form onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)}>
            <Card className="rounded-lg shadow-sm">
              <CardHeader>
                <CardTitle>{t("pages.accountSettings.changePassword")}</CardTitle>
                <CardDescription>
                  {t("pages.accountSettings.passwordDescription")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={passwordForm.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("pages.accountSettings.currentPassword")}</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={passwordForm.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("pages.accountSettings.newPassword")}</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormDescription>
                        {t("pages.accountSettings.passwordRequirement")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={passwordForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("pages.accountSettings.confirmPassword")}</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
              <CardFooter className="flex justify-end border-t px-6 py-4">
                <Button
                  type="submit"
                  size="sm"
                  disabled={passwordLoading}
                >
                  {passwordLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("pages.accountSettings.updatePassword")}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </Form>
      </div>
    </div>
  );
}
